/* ==========================================
   REAL-TIME MONITORING & CRASH REPORTING MODULE
   ========================================== */

const CRASH_LOG_DEBOUNCE_MS = 30000; // max 1 crash write / 30s / browser session
const MAX_MESSAGE_LENGTH = 600;
const MAX_REASON_LENGTH = 1200;
const MAX_FILENAME_LENGTH = 300;
const MAX_USER_AGENT_LENGTH = 300;
const MAX_STACK_LENGTH = 3000;
const MAX_SERIALIZED_PAYLOAD_LENGTH = 6000;

let lastCrashLogWriteAt = 0;

function truncateText(value, maxLength) {
  if (value == null) return "";
  const str = String(value);
  return str.length > maxLength ? `${str.slice(0, maxLength)}…[truncated]` : str;
}

function buildSafeErrorLog(payload) {
  const normalized = {
    type: truncateText(payload.type, 40),
    message: truncateText(payload.message, MAX_MESSAGE_LENGTH),
    reason: truncateText(payload.reason, MAX_REASON_LENGTH),
    filename: truncateText(payload.filename, MAX_FILENAME_LENGTH),
    lineno: Number.isFinite(payload.lineno) ? payload.lineno : null,
    colno: Number.isFinite(payload.colno) ? payload.colno : null,
    stack: truncateText(payload.stack, MAX_STACK_LENGTH),
    timestamp: new Date().toISOString(),
    userAgent: truncateText(navigator.userAgent, MAX_USER_AGENT_LENGTH),
    href: truncateText(window.location && window.location.href ? window.location.href : "", 400),
  };

  const serialized = JSON.stringify(normalized);
  if (serialized.length > MAX_SERIALIZED_PAYLOAD_LENGTH) {
    normalized.message = truncateText(normalized.message, 200);
    normalized.reason = truncateText(normalized.reason, 400);
    normalized.stack = truncateText(normalized.stack, 1200);
  }

  return normalized;
}

export function initMonitoring() {
  console.log('[Monitoring] Initializing real-time crash and performance reporting...');

  // Initialize Firebase Performance Monitoring if available
  if (typeof firebase !== 'undefined' && firebase.performance) {
    try {
      firebase.performance();
      console.log('[Monitoring] Firebase Performance Monitoring active.');
    } catch (e) {
      console.warn('[Monitoring] Firebase Performance init warning:', e);
    }
  }

  // Global uncaught error handler
  window.addEventListener('error', (event) => {
    const errorLog = buildSafeErrorLog({
      type: 'uncaught_error',
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error && event.error.stack ? event.error.stack : ""
    });
    console.error('[Monitoring Log]', errorLog);
    reportCrashToBackend(errorLog);
  });

  // Global unhandled promise rejection handler
  window.addEventListener('unhandledrejection', (event) => {
    const reasonObj = event.reason;
    const errorLog = buildSafeErrorLog({
      type: 'unhandled_rejection',
      message: reasonObj && reasonObj.message ? reasonObj.message : "",
      reason: reasonObj && reasonObj.message ? reasonObj.message : String(reasonObj),
      stack: reasonObj && reasonObj.stack ? reasonObj.stack : ""
    });
    console.error('[Monitoring Log]', errorLog);
    reportCrashToBackend(errorLog);
  });
}

window.initMonitoring = initMonitoring;

export function reportCrashToBackend(errorLog) {
  const now = Date.now();
  if (now - lastCrashLogWriteAt < CRASH_LOG_DEBOUNCE_MS) {
    console.warn('[Monitoring] Crash log throttled to protect Firestore quota.');
    return;
  }
  lastCrashLogWriteAt = now;

  if (typeof window !== "undefined" && window.Sentry && typeof window.Sentry.captureMessage === "function") {
    window.Sentry.captureMessage("client_crash", { level: "error", extra: buildSafeErrorLog(errorLog) });
  }

  if (typeof firebase === 'undefined' || !firebase.firestore) {
    console.warn('[Monitoring] Firebase Firestore unavailable; skipping crash write.');
    return;
  }

  try {
    const db = firebase.firestore();
    db.collection('clientCrashLogs').add(buildSafeErrorLog(errorLog)).catch((err) => {
      console.warn('[Monitoring] Failed to persist crash log:', err && err.message ? err.message : err);
    });
  } catch (err) {
    console.warn('[Monitoring] Failed to initialize crash logging transport:', err && err.message ? err.message : err);
  }
}
